import {
  useTheme,
  useMediaQuery,
  Box,
  Typography,
  Button,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  LinearProgress,
  CircularProgress,
} from '@mui/material';
import { BarChart, FlightTakeoff, Speed } from '@mui/icons-material';
import FlexCenter from 'components/FlexCenter';


const StatCard = ({ label, value, color, icon: Icon }) => (
  <Box
    sx={{
      p: 2.5,
      borderRadius: 2,
      bgcolor: 'action.hover',
      border: '1px solid', borderColor: 'divider',
      textAlign: 'center',
    }}
  >
    {Icon && (
      <Box display="flex" justifyContent="center" mb={0.5}>
        <Icon sx={{ color, fontSize: 28, opacity: 0.8 }} />
      </Box>
    )}
    <Typography variant="h3" fontWeight="bold" sx={{ color, lineHeight: 1.1 }}>
      {value ?? '—'}
    </Typography>
    <Typography variant="caption" color="text.secondary" display="block" mt={0.5}>
      {label}
    </Typography>
  </Box>
);

const SeasonStatsDialog = ({ open, onClose, seasonName, statsData }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const maxRouteFreq = statsData?.topRoutes?.[0]?.weeklyFreq || 1;

  return (
    <Dialog 
      fullWidth 
      fullScreen={isMobile}
      maxWidth="sm" 
      open={open} 
      onClose={onClose} 
    >
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <BarChart fontSize="small" color="primary" />
          <Box>
            <Typography variant="subtitle1" fontWeight="bold" lineHeight={1.2}>
              Season Statistics
            </Typography>
            <Typography variant="caption" color="text.secondary">{seasonName}</Typography>
          </Box>
        </Box>
      </DialogTitle>

      <DialogContent dividers>
        {!statsData ? (
          <FlexCenter py={6}>
            <CircularProgress />
          </FlexCenter>
        ) : (
          <>
            {/* Stat cards */}
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <StatCard
                label="Total Patterns"
                value={statsData.totalFlights}
                color={theme.palette.primary.main}
                icon={FlightTakeoff}
              />
              <StatCard
                label="Weekly Flights"
                value={statsData.weeklyFrequency}
                color={theme.palette.secondary.main}
                icon={Speed}
              />
            </Box>

            {/* By aircraft type */}
            {statsData.byAcType?.length > 0 && (
              <>
                <Divider sx={{ mb: 2 }} />
                <Typography variant="subtitle2" fontWeight="bold" mb={1.5}>
                  By Aircraft Type
                </Typography>
                <Box display="flex" flexWrap="wrap" gap={1} mb={1}>
                  {statsData.byAcType.map((item) => (
                    <Chip
                      key={item._id}
                      label={`${item._id || 'N/A'} · ${item.count}`}
                      size="small"
                      variant="outlined"
                    />
                  ))}
                </Box>
              </>
            )}

            {/* Top routes */}
            {statsData.topRoutes?.length > 0 && (
              <>
                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" fontWeight="bold" mb={2}>
                  Top Routes
                </Typography>
                <Box display="flex" flexDirection="column" gap={1.75}>
                  {statsData.topRoutes.slice(0, 10).map((r) => (
                    <Box key={`${r._id.dep}-${r._id.arr}`}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={0.5}>
                        <Typography variant="body2" fontWeight="medium">
                          {r._id.dep} → {r._id.arr}
                        </Typography>
                        <Chip
                          label={`${r.weeklyFreq}×/wk`}
                          size="small"
                          color="primary"
                          variant="outlined"
                          sx={{ fontSize: '0.7rem', height: 20 }}
                        />
                      </Box>
                      <LinearProgress
                        variant="determinate"
                        value={(r.weeklyFreq / maxRouteFreq) * 100}
                        sx={{ height: 5, borderRadius: 1 }}
                      />
                    </Box>
                  ))}
                </Box>
              </>
            )}
          </>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: { xs: 3, sm: 2 } }}>
        <Button
          variant="outlined"
          color="secondary"
          fullWidth={isMobile}
          onClick={onClose}
        >
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SeasonStatsDialog;
